import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ContextMenu, ContextMenuItem } from '@repo/design-system/components/ui/context-menu';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { hideAllRegionPins, selectExternalMapId, selectHiddenRegions, selectMapData, showAllRegionPins, showOnlyRegionPins, toggleRegionPinVisibility } from '@repo/store/maps/slices/mapData';
import { useMemo, useState } from 'react';
const regionsStyles = css({
  display: 'grid',
  gap: '4',
  width: 'full',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
  _compact: {
    gridTemplateColumns: '1fr'
  }
});
const regionSwatchStyles = css({
  width: '[16px]',
  minWidth: '[16px]',
  textAlign: 'center'
});
const checkboxStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4',
  minWidth: '[16px]',
  minHeight: '[16px]',
  width: '[16px]',
  height: '[16px]',
  position: 'relative',
  flexShrink: 0,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'whiteAlpha.30',
  borderRadius: '4'
});
interface MapRegionFiltersProps {
  isMobile?: boolean;
}
export const MapRegionFilters: React.FC<MapRegionFiltersProps> = t0 => {
  const $ = _c(70);
  const {
    isMobile: t1
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const [isExpanded, setIsExpanded] = useState(false);
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapData = useAppSelector(selectMapData);
  const hiddenRegions = useAppSelector(selectHiddenRegions);
  let t3;
  if ($[1] !== mapData?.regions?.features) {
    t3 = mapData?.regions?.features ?? [];
    $[1] = mapData?.regions?.features;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const regions = t3;
  let t4;
  if ($[3] !== hiddenRegions) {
    t4 = new Set(hiddenRegions);
    $[3] = hiddenRegions;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const hiddenRegionIds = t4;
  if (regions.length === 0) {
    return null;
  }
  let t5;
  if ($[5] !== analytics || $[6] !== externalMapId || $[7] !== hostType) {
    t5 = (filtered, regionId, source) => {
      if (analytics && externalMapId && hostType) {
        analytics.event("game_map_toggle_region", {
          mapId: externalMapId,
          regionId,
          filtered,
          platform: hostType,
          ...(source ? {
            source
          } : {})
        });
      }
    };
    $[5] = analytics;
    $[6] = externalMapId;
    $[7] = hostType;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const trackRegionToggle = t5;
  let t6;
  if ($[9] !== dispatch || $[10] !== trackRegionToggle) {
    t6 = () => {
      dispatch(showAllRegionPins());
      trackRegionToggle(false, undefined, "sidebar-context-menu");
    };
    $[9] = dispatch;
    $[10] = trackRegionToggle;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleShowAllRegions = t6;
  let t7;
  if ($[12] !== dispatch || $[13] !== trackRegionToggle) {
    t7 = () => {
      dispatch(hideAllRegionPins());
      trackRegionToggle(true, undefined, "sidebar-context-menu");
    };
    $[12] = dispatch;
    $[13] = trackRegionToggle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleHideAllRegions = t7;
  let t8;
  if ($[15] !== dispatch || $[16] !== trackRegionToggle) {
    t8 = regionId_0 => {
      dispatch(showOnlyRegionPins(regionId_0));
      trackRegionToggle(true, regionId_0, "sidebar-context-menu");
    };
    $[15] = dispatch;
    $[16] = trackRegionToggle;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleShowOnlyRegion = t8;
  let t9;
  if ($[18] !== dispatch || $[19] !== trackRegionToggle) {
    t9 = (regionId_1, isCurrentlyHidden, source_0) => {
      dispatch(toggleRegionPinVisibility(regionId_1));
      trackRegionToggle(!isCurrentlyHidden, regionId_1, source_0);
    };
    $[18] = dispatch;
    $[19] = trackRegionToggle;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleToggleRegionVisibility = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css.raw({
      contentVisibility: "auto",
      containIntrinsicSize: "[auto 80px]"
    });
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = css({
      flexGrow: 1,
      minWidth: "[0]"
    });
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== isExpanded) {
    t12 = () => setIsExpanded(!isExpanded);
    $[23] = isExpanded;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = css({
      justifyContent: "start",
      _active: {
        transform: "scale(0.97)",
        opacity: "0.8"
      }
    });
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = isExpanded ? "keyboard_arrow_down" : "keyboard_arrow_right";
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = css({
      marginLeft: "-4"
    });
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <Icon icon={t14} decorative={true} className={t15} />;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = css.raw({
      minWidth: "[0]"
    });
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t) {
    t18 = t(_temp);
    $[31] = t;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t18) {
    t19 = <Type variant="caption">{t18}</Type>;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t17 || $[36] !== t19) {
    t20 = <FlexRow align="center" gap="6" className={t17}>{t19}</FlexRow>;
    $[35] = t17;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t16 || $[41] !== t20) {
    t21 = <Button variant="ghost" onClick={t12} color="primary" size="sm" radius="rounded" fullWidth={true} className={t13}>{t16}{t20}</Button>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t16;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t(_temp2);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== handleShowAllRegions || $[46] !== t22) {
    t23 = <ContextMenuItem value="show-all-regions" leadingIcon="visibility" onSelect={handleShowAllRegions}>{t22}</ContextMenuItem>;
    $[45] = handleShowAllRegions;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t) {
    t24 = t(_temp3);
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== handleHideAllRegions || $[51] !== t24) {
    t25 = <ContextMenuItem value="hide-all-regions" leadingIcon="visibility_off" onSelect={handleHideAllRegions}>{t24}</ContextMenuItem>;
    $[50] = handleHideAllRegions;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t11 || $[54] !== t21 || $[55] !== t23 || $[56] !== t25) {
    t26 = <FlexRow width="full" align="center" gap="12"><ContextMenu triggerClassName={t11} trigger={t21}>{t23}{t25}</ContextMenu></FlexRow>;
    $[53] = t11;
    $[54] = t21;
    $[55] = t23;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== handleShowOnlyRegion || $[59] !== handleToggleRegionVisibility || $[60] !== hiddenRegionIds || $[61] !== isExpanded || $[62] !== isMobile || $[63] !== regions || $[64] !== t) {
    t27 = isExpanded && <div className={regionsStyles}>{regions.map(region => {
        const regionId_2 = region.properties.externalId;
        const regionName = region.properties.title || regionId_2;
        const isRegionHidden = hiddenRegionIds.has(regionId_2);
        const regionColor = region.properties.color ?? "rgba(255, 255, 255, 0.45)";
        return <ContextMenu key={regionId_2} trigger={<Button variant="ghost" onClick={() => handleToggleRegionVisibility(regionId_2, isRegionHidden)} color="primary" size="sm" radius="rounded" fullWidth={true} title={isRegionHidden ? t(_temp4, {
          regionName
        }) : t(_temp5, {
          regionName
        })} className={css({
          opacity: isRegionHidden ? "0.35" : "1",
          _active: {
            transform: "scale(0.97)",
            opacity: "0.8"
          },
          ...(isMobile ? {
            minHeight: "[44px]"
          } : {})
        })}><span className={regionSwatchStyles}><span className={css({
              display: "inline-flex",
              width: "[8px]",
              height: "[8px]",
              borderRadius: "full"
            })} style={{
              backgroundColor: regionColor
            }} /></span><Type color="whiteAlpha.80" variant="body-xs" truncate={true}>{regionName}</Type><div className={css({
            marginLeft: "auto"
          })}><div className={checkboxStyles} style={{
              backgroundColor: regionColor
            }}>{!isRegionHidden && <Icon className={css({
                position: "absolute"
              })} fill={true} icon="check" size="md" decorative={true} />}</div></div></Button>}><ContextMenuItem value="show-only-region" leadingIcon="filter_alt" onSelect={() => handleShowOnlyRegion(regionId_2)}>{t(_temp6, {
              regionName
            })}</ContextMenuItem><ContextMenuItem value="toggle-visibility" leadingIcon={isRegionHidden ? "visibility" : "visibility_off"} onSelect={() => handleToggleRegionVisibility(regionId_2, isRegionHidden, "sidebar-context-menu")}>{isRegionHidden ? t(_temp7, {
              regionName
            }) : t(_temp8, {
              regionName
            })}</ContextMenuItem></ContextMenu>;
      })}</div>;
    $[58] = handleShowOnlyRegion;
    $[59] = handleToggleRegionVisibility;
    $[60] = hiddenRegionIds;
    $[61] = isExpanded;
    $[62] = isMobile;
    $[63] = regions;
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t10 || $[67] !== t26 || $[68] !== t27) {
    t28 = <FlexColumn width="full" className={t10}>{t26}{t27}</FlexColumn>;
    $[66] = t10;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  return t28;
};
function _temp($) {
  return $.map_regions;
}
function _temp2($_0) {
  return $_0.show_all_regions;
}
function _temp3($_1) {
  return $_1.hide_all_regions;
}
function _temp4($_2) {
  return $_2.show_pins_in_region;
}
function _temp5($_3) {
  return $_3.hide_pins_in_region;
}
function _temp6($_4) {
  return $_4.show_only_pins_in_region;
}
function _temp7($_5) {
  return $_5.show_pins_in_region;
}
function _temp8($_6) {
  return $_6.hide_pins_in_region;
}